import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, Save, Pin, Star, Eye, Upload, Loader2,
  Youtube, ImagePlus, Archive as ArchiveIcon, Send, FileText, Share2,
} from "lucide-react";
import api from "../../lib/api";
import SocialShareModal from "./SocialShareModal";

const STATUS_LABELS = {
  draft: { label: "Brouillon", cls: "bg-foreground/10 text-foreground/70" },
  published: { label: "Publiée", cls: "bg-emerald-100 text-emerald-700" },
  archived: { label: "Archivée", cls: "bg-orange-100 text-orange-700" },
};

const NEWS_DEFAULTS = {
  title: "", excerpt: "", body: "",
  cover_image: "", cover_public_id: "",
  images: [],
  youtube_urls: [],
  category: "Actualités",
  location: "",
  tags: [],
  status: "published",
  published_at: "",
  is_featured: false,
  is_pinned: false,
  pinned_order: 0,
  seo_title: "",
  seo_description: "",
  seo_image: "",
};

async function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function NewsAdmin() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [sharing, setSharing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [uploadStatus, setUploadStatus] = useState({ cloudinary_configured: false });

  const load = () => {
    api.get("/admin/news").then((r) => setItems(r.data));
    api.get("/news-categories").then((r) => setCategories(r.data));
    api.get("/admin/upload/status").then((r) => setUploadStatus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (f) => {
    try {
      // Nettoyage : retire chaînes vides des tableaux
      const payload = {
        ...f,
        youtube_urls: (f.youtube_urls || []).filter(Boolean),
        images: (f.images || []).filter((i) => i && i.url),
        tags: (f.tags || []).filter(Boolean),
      };
      if (editing?.id) await api.put(`/admin/news/${editing.id}`, payload);
      else await api.post("/admin/news", payload);
      toast.success("Actualité enregistrée");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => {
    if (!confirm("Supprimer cette actualité ?")) return;
    await api.delete(`/admin/news/${id}`);
    toast.success("Actualité supprimée");
    load();
  };
  const togglePin = async (id) => { await api.post(`/admin/news/${id}/toggle-pin`); load(); };
  const toggleFeatured = async (id) => { await api.post(`/admin/news/${id}/toggle-featured`); load(); };

  const filtered = items.filter((n) => {
    if (statusFilter !== "all" && n.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (n.title || "").toLowerCase().includes(s) || (n.category || "").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div data-testid="news-admin">
      {!uploadStatus.cloudinary_configured && (
        <div className="mb-4 bg-orange-50 border border-orange-200 rounded-sm p-3 text-xs text-orange-800 flex items-center gap-2">
          <ImagePlus className="w-4 h-4 shrink-0" />
          <span><strong>Cloudinary non configuré :</strong> les images sont stockées en base64 (fallback). Ajoutez les clés CLOUDINARY_* dans <code>backend/.env</code> pour activer l'optimisation automatique.</span>
        </div>
      )}

      {/* Header + filtres */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">Rechercher</label>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Titre ou catégorie…"
                 className="w-full border border-border rounded-sm p-2.5 text-sm focus:border-ink outline-none"
                 data-testid="news-search" />
        </div>
        <div>
          <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">Statut</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-border rounded-sm p-2.5 text-sm bg-white focus:border-ink outline-none"
                  data-testid="news-status-filter">
            <option value="all">Tous</option>
            <option value="published">Publiées</option>
            <option value="draft">Brouillons</option>
            <option value="archived">Archivées</option>
          </select>
        </div>
        <button onClick={() => setEditing({ ...NEWS_DEFAULTS })} className="btn-primary" data-testid="news-create">
          <Plus className="w-4 h-4" /> Nouvelle actualité
        </button>
      </div>

      {/* Liste */}
      <div className="bg-white border border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bone text-foreground/70 text-[0.65rem] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5 font-semibold">Titre</th>
              <th className="text-left px-4 py-2.5 font-semibold">Catégorie</th>
              <th className="text-left px-4 py-2.5 font-semibold">Statut</th>
              <th className="text-left px-4 py-2.5 font-semibold">Vues</th>
              <th className="text-left px-4 py-2.5 font-semibold">Flags</th>
              <th className="text-left px-4 py-2.5 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((n) => {
              const st = STATUS_LABELS[n.status] || STATUS_LABELS.draft;
              return (
                <tr key={n.id} className="border-t border-border" data-testid={`news-row-${n.id}`}>
                  <td className="px-4 py-2.5 max-w-md">
                    <div className="font-semibold text-ink truncate">{n.title}</div>
                    <div className="text-[0.7rem] text-foreground/50 mt-0.5">/{n.slug}</div>
                  </td>
                  <td className="px-4 py-2.5">{n.category}</td>
                  <td className="px-4 py-2.5">
                    <span className={`text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-2.5 text-xs flex items-center gap-1.5"><Eye className="w-3 h-3 text-foreground/40" /> {n.view_count || 0}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      {n.is_featured && <span className="text-[0.6rem] uppercase tracking-wider bg-gold/25 text-ink px-2 py-0.5 rounded">À la une</span>}
                      {n.is_pinned && <span className="text-[0.6rem] uppercase tracking-wider bg-ink text-white px-2 py-0.5 rounded flex items-center gap-1"><Pin className="w-2.5 h-2.5" /> Épinglée</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSharing(n)} title="Partager sur les réseaux sociaux"
                              data-testid={`news-share-${n.id}`}
                              className="p-1.5 text-foreground/50 hover:text-gold-dark rounded-sm">
                        <Share2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => togglePin(n.id)} title={n.is_pinned ? "Désépingler" : "Épingler"}
                              data-testid={`news-pin-${n.id}`}
                              className={`p-1.5 rounded-sm ${n.is_pinned ? "bg-ink text-gold" : "text-foreground/50 hover:text-ink"}`}>
                        <Pin className="w-4 h-4" />
                      </button>
                      <button onClick={() => toggleFeatured(n.id)} title={n.is_featured ? "Retirer À la une" : "Mettre à la une"}
                              data-testid={`news-featured-${n.id}`}
                              className={`p-1.5 rounded-sm ${n.is_featured ? "bg-gold text-ink" : "text-foreground/50 hover:text-ink"}`}>
                        <Star className="w-4 h-4" />
                      </button>
                      <button onClick={() => setEditing(n)} title="Modifier" data-testid={`news-edit-${n.id}`}
                              className="p-1.5 text-foreground/50 hover:text-ink rounded-sm">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => del(n.id)} title="Supprimer" data-testid={`news-del-${n.id}`}
                              className="p-1.5 text-foreground/50 hover:text-crimson rounded-sm">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-foreground/55">Aucune actualité.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <NewsFormModal
          key={editing.id || "new"}
          value={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSave={save}
          onShare={() => { setSharing(editing); }}
        />
      )}
      {sharing && (
        <SocialShareModal news={sharing} onClose={() => setSharing(null)} />
      )}
    </div>
  );
}

/* ============ Modal Formulaire ============ */
function NewsFormModal({ value, categories, onClose, onSave, onShare }) {
  const [f, setF] = useState(value);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const uploadCover = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const data = await fileToDataURL(file);
      const r = await api.post("/admin/upload/image", { data, folder: "frontunirdc/news/covers" });
      set("cover_image", r.data.url);
      set("cover_public_id", r.data.public_id || "");
      toast.success(r.data.fallback ? "Image encodée (fallback base64)" : "Image téléversée sur Cloudinary");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'upload");
    } finally { setUploading(false); }
  };

  const addGalleryImages = async (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    setUploading(true);
    const uploaded = [];
    try {
      for (const file of arr) {
        const data = await fileToDataURL(file);
        const r = await api.post("/admin/upload/image", { data, folder: "frontunirdc/news/gallery" });
        uploaded.push({ url: r.data.url, public_id: r.data.public_id || null, caption: "" });
      }
      set("images", [...(f.images || []), ...uploaded]);
      toast.success(`${uploaded.length} image(s) ajoutée(s)`);
    } catch (e) {
      toast.error("Échec de l'upload");
    } finally { setUploading(false); }
  };

  const submit = async () => {
    if (!f.title?.trim()) return toast.error("Le titre est obligatoire");
    if (!f.body?.trim()) return toast.error("Le corps de l'article est obligatoire");
    setSaving(true);
    try { await onSave(f); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4 sm:p-6" onClick={onClose}>
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" />
      <div className="relative bg-white w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-sm shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white z-10 border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <div className="eyebrow text-gold-dark">{f.id ? "Modification" : "Nouvelle actualité"}</div>
            <h3 className="font-serif text-xl text-ink mt-0.5 truncate max-w-md">{f.title || "Sans titre"}</h3>
          </div>
          <button onClick={onClose} className="text-foreground/60 hover:text-ink"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Section 1 : contenu principal */}
          <SectionTitle>Contenu</SectionTitle>
          <FieldLabel label="Titre *">
            <input value={f.title || ""} onChange={(e) => set("title", e.target.value)}
                   className="inp" data-testid="news-title" placeholder="Titre de l'article" />
          </FieldLabel>
          <div className="grid md:grid-cols-2 gap-4">
            <FieldLabel label="Catégorie">
              <select value={f.category || ""} onChange={(e) => set("category", e.target.value)}
                      className="inp" data-testid="news-category">
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </FieldLabel>
            <FieldLabel label="Lieu (optionnel)">
              <input value={f.location || ""} onChange={(e) => set("location", e.target.value)}
                     className="inp" data-testid="news-location" placeholder="Ex : Bruxelles, Belgique" />
            </FieldLabel>
          </div>
          <FieldLabel label="Chapô / résumé *">
            <textarea rows={2} value={f.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)}
                      className="inp" data-testid="news-excerpt" placeholder="Résumé qui apparaîtra dans les listes et le partage" />
          </FieldLabel>
          <FieldLabel label="Corps de l'article *">
            <textarea rows={10} value={f.body || ""} onChange={(e) => set("body", e.target.value)}
                      className="inp font-serif" data-testid="news-body" placeholder="Contenu complet — sauts de ligne préservés" />
          </FieldLabel>
          <FieldLabel label="Tags (séparés par des virgules)">
            <input value={(f.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
                   className="inp" data-testid="news-tags" placeholder="rdc, plaidoyer, europe…" />
          </FieldLabel>

          {/* Section 2 : image de couverture */}
          <SectionTitle>Image de couverture</SectionTitle>
          <div className="grid md:grid-cols-[220px,1fr] gap-4 items-start">
            <div className="aspect-[4/3] rounded-sm bg-ink/5 border border-dashed border-border overflow-hidden grid place-items-center relative">
              {f.cover_image ? (
                <img src={f.cover_image} alt="cover" className="w-full h-full object-cover" />
              ) : (
                <ImagePlus className="w-8 h-8 text-foreground/30" />
              )}
              {uploading && <div className="absolute inset-0 bg-ink/60 grid place-items-center"><Loader2 className="w-6 h-6 text-white animate-spin" /></div>}
            </div>
            <div className="space-y-3">
              <label className="btn-outline text-sm cursor-pointer inline-flex" data-testid="news-cover-upload">
                <Upload className="w-4 h-4" />
                <span>Téléverser une image</span>
                <input type="file" accept="image/*" hidden onChange={(e) => uploadCover(e.target.files?.[0])} />
              </label>
              <div className="text-xs text-foreground/55">
                Formats acceptés : JPG, PNG, WEBP. Optimisation automatique via Cloudinary.
              </div>
              <FieldLabel label="URL directe (alternative)">
                <input value={f.cover_image || ""} onChange={(e) => { set("cover_image", e.target.value); set("cover_public_id", ""); }}
                       className="inp text-xs" placeholder="https://..." data-testid="news-cover-url" />
              </FieldLabel>
            </div>
          </div>

          {/* Section 3 : galerie photos */}
          <SectionTitle>Galerie photos (images secondaires)</SectionTitle>
          <div className="space-y-3">
            <label className="btn-outline text-sm cursor-pointer inline-flex" data-testid="news-gallery-upload">
              <Upload className="w-4 h-4" />
              <span>Ajouter des images</span>
              <input type="file" accept="image/*" multiple hidden onChange={(e) => addGalleryImages(e.target.files)} />
            </label>
            {(f.images || []).length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {(f.images || []).map((img, i) => (
                  <div key={i} className="relative aspect-square rounded-sm overflow-hidden bg-ink/5 group">
                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => set("images", (f.images || []).filter((_, j) => j !== i))}
                            className="absolute top-1 right-1 bg-crimson text-white p-1 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`news-gallery-del-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 4 : liens YouTube */}
          <SectionTitle>Liens YouTube</SectionTitle>
          <div className="space-y-2">
            {(f.youtube_urls || []).map((u, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Youtube className="w-4 h-4 text-crimson shrink-0" />
                <input value={u} onChange={(e) => {
                  const arr = [...(f.youtube_urls || [])]; arr[i] = e.target.value; set("youtube_urls", arr);
                }} className="inp flex-1" placeholder="https://www.youtube.com/watch?v=..." data-testid={`news-yt-${i}`} />
                <button onClick={() => set("youtube_urls", (f.youtube_urls || []).filter((_, j) => j !== i))}
                        className="text-foreground/50 hover:text-crimson">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button onClick={() => set("youtube_urls", [...(f.youtube_urls || []), ""])}
                    className="text-sm text-ink font-semibold inline-flex items-center gap-1.5 hover:text-gold-dark" data-testid="news-yt-add">
              <Plus className="w-3.5 h-3.5" /> Ajouter un lien YouTube
            </button>
          </div>

          {/* Section 5 : publication */}
          <SectionTitle>Publication</SectionTitle>
          <div className="grid md:grid-cols-2 gap-4">
            <FieldLabel label="Statut">
              <select value={f.status || "published"} onChange={(e) => set("status", e.target.value)}
                      className="inp" data-testid="news-status">
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
                <option value="archived">Archivé</option>
              </select>
            </FieldLabel>
            <FieldLabel label="Programmer (date & heure)">
              <input type="datetime-local"
                     value={toLocalDT(f.published_at)}
                     onChange={(e) => set("published_at", fromLocalDT(e.target.value))}
                     className="inp" data-testid="news-scheduled" />
              <div className="text-[0.7rem] text-foreground/55 mt-1">Vide = maintenant. Une date future rend l'article visible à cette heure.</div>
            </FieldLabel>
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!f.is_featured} onChange={(e) => set("is_featured", e.target.checked)} data-testid="news-featured-cb" />
              <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 text-gold-dark" /> À la une (hero)</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={!!f.is_pinned} onChange={(e) => set("is_pinned", e.target.checked)} data-testid="news-pinned-cb" />
              <span className="inline-flex items-center gap-1"><Pin className="w-3.5 h-3.5" /> Épinglée en tête</span>
            </label>
            {f.is_pinned && (
              <FieldLabel label="Ordre d'épinglage" inline>
                <input type="number" value={f.pinned_order ?? 0} onChange={(e) => set("pinned_order", Number(e.target.value) || 0)}
                       className="inp w-24" data-testid="news-pin-order" />
              </FieldLabel>
            )}
          </div>

          {/* Section 6 : SEO */}
          <SectionTitle>Référencement (SEO)</SectionTitle>
          <div className="grid md:grid-cols-2 gap-4">
            <FieldLabel label="Titre SEO (optionnel)">
              <input value={f.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)}
                     className="inp" data-testid="news-seo-title" placeholder="Par défaut : titre de l'article" />
            </FieldLabel>
            <FieldLabel label="Image de partage (URL)">
              <input value={f.seo_image || ""} onChange={(e) => set("seo_image", e.target.value)}
                     className="inp" data-testid="news-seo-image" placeholder="Par défaut : image de couverture" />
            </FieldLabel>
          </div>
          <FieldLabel label="Description SEO">
            <textarea rows={2} value={f.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)}
                      className="inp" data-testid="news-seo-desc" placeholder="Par défaut : chapô" />
          </FieldLabel>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-border px-6 py-4 flex items-center justify-between gap-3">
          <div className="text-xs text-foreground/55">
            {f.id ? `Modification de l'article — ${f.view_count || 0} vues` : "Nouvel article"}
          </div>
          <div className="flex items-center gap-2">
            {f.id && onShare && (
              <button onClick={onShare} className="btn-outline text-sm" data-testid="news-share-from-form">
                <Share2 className="w-4 h-4" /> Partager sur les réseaux sociaux
              </button>
            )}
            <button onClick={onClose} className="btn-outline text-sm" data-testid="news-cancel">Annuler</button>
            <button onClick={submit} disabled={saving} className="btn-primary text-sm" data-testid="news-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="pt-2">
      <div className="eyebrow text-gold-dark">{children}</div>
      <div className="h-px bg-border mt-2" />
    </div>
  );
}

function FieldLabel({ label, children, inline = false }) {
  return (
    <div className={inline ? "inline-flex items-center gap-2" : ""}>
      <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

/* Convert ISO to <input type="datetime-local"> and back */
function toLocalDT(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    // formatted as YYYY-MM-DDTHH:mm (local)
    const pad = (n) => n.toString().padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}
function fromLocalDT(local) {
  if (!local) return "";
  try { return new Date(local).toISOString(); } catch { return ""; }
}
